import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button, Container, Row } from 'reactstrap';
import { refreshCatalog } from './form/shared/CatalogAutocomplete';
import config from '../../../config';
import TableBuilder from '../../../shared/components/table/TableBuilder';
import {
  currencyFormatter, getExportPrefix,
} from '../../../shared/components/table/functions';
// import { imageFieldColumn, imageCellRendererComponent } from '../../../../shared/components/table/ImageCellRenderer';
import CardPanel from '../../../shared/components/widgets/CardPanel';
import { doFetch } from '../../../helpers';
import { countryOptionsArray, countryOptionDefault } from '../../Onboarding/amazon/components/OnboardingConfig';
import ItemEditModal from './form/ItemEditModal';
import MetaProductSetsTable from './MetaProductSetsTable';

function SimpleCatalogTable({ retailerConfig, forceRefresh, setForceRefresh, budgetGroupOptions }) {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedData, setSelectedData] = useState(0);
  const [agGrid, setAgGrid] = useState(null);

  // Token cache for Google's pagination (offset -> token mapping)
  const tokenCacheRef = useRef({ 0: null });
  // Cursor cache for Meta's pagination (offset -> cursor mapping)
  const cursorCacheRef = useRef({ 0: null });

  // Determine page size based on platform
  let pageSize = 5000; // Default for Amazon/Walmart/Target
  if (retailerConfig.key === 'google') {
    pageSize = 250;
  } else if (retailerConfig.key === 'meta') {
    pageSize = 100;
  } else if (retailerConfig.key === 'tiktok') {
    pageSize = 100;
  }

  const exportPrefix = getExportPrefix(`${retailerConfig.prefix}-catalog`, selectedSeller, true);

  const amzColumnDefs = [
    // imageFieldColumn,
    {
      field: 'asin',
      headerName: 'ASIN',
      cellRenderer: (params) => {
        if (params && params.data) {
          const getMarketPlace = (name) => {
            let result = countryOptionDefault.marketplace;
            countryOptionsArray.forEach((country) => {
              if (name === country.value) {
                result = country.marketplace;
              }
            });
            return result;
          };
          const { mpName } = selectedSeller;
          const marketplace = getMarketPlace(mpName);
          const url = `https://${marketplace}/dp/${params.data.asin}`;
          return <a href={url} target="_blank" rel="noopener noreferrer">{params.data.asin}</a>;
        }
        return '';
      },
    },
    {
      field: 'sku',
      headerName: 'SKU',
    },
    { field: 'product_title', headerName: 'Product Title' },
    {
      field: 'list_price',
      headerName: 'List Price',
      hide: true,
      valueFormatter: currencyFormatter,
    },
    { field: 'budget_group', headerName: 'Budget Group' },
    {
      field: 'ad_status',
      headerName: 'Ad Status',
    },
    {
      field: 'reporting_visibility',
      headerName: 'Reporting Visibility',
    },
    {
      field: 'brand',
      headerName: 'Brand',
      headerTooltip: 'ASIN Brand used in reports',
    },
    {
      field: 'subcategory',
      headerName: 'Subcategory',
      headerTooltip: 'ASIN Subcategory used in reports',
    },
  ];

  const wmColumnDefs = [
    {
      field: 'item_id',
      headerName: 'Item ID',
    },
    {
      field: 'sku',
      headerName: 'SKU',
    },
    { field: 'product_name', headerName: 'Product Name' },
    {
      field: 'price',
      headerName: 'Price',
      hide: true,
      valueFormatter: currencyFormatter,
    },
    { field: 'budget_group', headerName: 'Budget Group' },
    {
      field: 'ad_status',
      headerName: 'Ad Status',
    },
    {
      field: 'reporting_visibility',
      headerName: 'Reporting Visibility',
    },
    {
      field: 'brand',
      headerName: 'Brand',
    },
    {
      field: 'primary_cat_path',
      headerName: 'Category Path',
    },
  ];

  const targetColumnDefs = [
    {
      field: 'item_id',
      headerName: 'Item ID',
    },
    {
      field: 'sku',
      headerName: 'SKU',
    },
    { field: 'product_name', headerName: 'Product Name' },
    {
      field: 'price',
      headerName: 'Price',
      hide: true,
      valueFormatter: currencyFormatter,
    },
    { field: 'budget_group', headerName: 'Budget Group' },
    {
      field: 'ad_status',
      headerName: 'Ad Status',
    },
    {
      field: 'reporting_visibility',
      headerName: 'Reporting Visibility',
    },
    {
      field: 'brand',
      headerName: 'Brand',
    },
    {
      field: 'primary_cat_path',
      headerName: 'Category Path',
    },
  ];

  const googleColumnDefs = [
    {
      field: 'image_link',
      headerName: 'Image',
      width: 100,
      cellRenderer: (params) => {
        if (params.data?.image_link) {
          return (
            <img
              src={params.data.image_link}
              alt="Product"
              style={{ maxWidth: '75px', maxHeight: '75px', objectFit: 'contain' }}
            />
          );
        }
        return '';
      },
    },
    {
      field: 'item_id',
      headerName: 'Item ID',
      cellRenderer: (params) => {
        if (params.data?.link) {
          return (
            <a
              href={params.data.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {params.data.item_id}
            </a>
          );
        }
        return params.data?.item_id || '';
      },
    },
    {
      field: 'sku',
      headerName: 'SKU',
    },
    { field: 'title', headerName: 'Title' },
    {
      field: 'price',
      headerName: 'Price',
      valueFormatter: currencyFormatter,
    },
    {
      field: 'sale_price',
      headerName: 'Sale Price',
      valueFormatter: currencyFormatter,
    },
    { field: 'budget_group_id', headerName: 'Budget Group ID' },
    {
      field: 'ad_status',
      headerName: 'Ad Status',
    },
    {
      field: 'reporting_visibility',
      headerName: 'Reporting Visibility',
    },
    {
      field: 'brand',
      headerName: 'Brand',
    },
    {
      field: 'product_type',
      headerName: 'Product Type',
    },
    {
      field: 'google_product_category',
      headerName: 'Google Category',
    },
    {
      field: 'size',
      headerName: 'Size',
    },
    {
      field: 'color',
      headerName: 'Color',
    },
    {
      field: 'availability',
      headerName: 'Availability',
    },
    {
      field: 'condition_type',
      headerName: 'Condition',
    },
    {
      field: 'merchant_id',
      headerName: 'Merchant ID',
    },
    {
      field: 'gtin',
      headerName: 'GTIN',
    },
    {
      field: 'mpn',
      headerName: 'MPN',
    },
  ];

  const metaColumnDefs = [
    {
      field: 'image_url',
      headerName: 'Image',
      width: 100,
      cellRenderer: (params) => {
        if (params.data?.image_url) {
          return (
            <img
              src={params.data.image_url}
              alt="Product"
              style={{ maxWidth: '75px', maxHeight: '75px', objectFit: 'contain' }}
            />
          );
        }
        return '';
      },
    },
    {
      field: 'meta_product_id',
      headerName: 'Meta Product ID',
      cellRenderer: (params) => {
        if (params.data?.product_url) {
          return (
            <a
              href={params.data.product_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {params.data.meta_product_id}
            </a>
          );
        }
        return params.data?.meta_product_id || '';
      },
    },
    {
      field: 'retailer_id',
      headerName: 'Retailer ID',
    },
    { field: 'title', headerName: 'Title' },
    { field: 'description', headerName: 'Description' },
    {
      field: 'price',
      headerName: 'Price',
      valueFormatter: currencyFormatter,
    },
    {
      field: 'sale_price',
      headerName: 'Sale Price',
      valueFormatter: currencyFormatter,
    },
    { field: 'budget_group_id', headerName: 'Budget Group ID' },
    {
      field: 'ad_status',
      headerName: 'Ad Status',
    },
    {
      field: 'reporting_visibility',
      headerName: 'Reporting Visibility',
    },
    {
      field: 'brand',
      headerName: 'Brand',
    },
    {
      field: 'product_type',
      headerName: 'Product Type',
    },
    {
      field: 'category',
      headerName: 'Category',
    },
    {
      field: 'size',
      headerName: 'Size',
    },
    {
      field: 'color',
      headerName: 'Color',
    },
    {
      field: 'availability',
      headerName: 'Availability',
    },
    {
      field: 'condition',
      headerName: 'Condition',
    },
    {
      field: 'inventory',
      headerName: 'Inventory',
    },
    {
      field: 'gtin',
      headerName: 'GTIN',
    },
    {
      field: 'mpn',
      headerName: 'MPN',
    },
    {
      field: 'status',
      headerName: 'Status',
    },
  ];

  const tiktokColumnDefs = [
    {
      field: 'image_url',
      headerName: 'Image',
      width: 100,
      cellRenderer: (params) => {
        if (params.data?.image_url) {
          return (
            <img
              src={params.data.image_url}
              alt="Product"
              style={{ maxWidth: '75px', maxHeight: '75px', objectFit: 'contain' }}
            />
          );
        }
        return '';
      },
    },
    {
      field: 'product_id',
      headerName: 'Product ID',
    },
    {
      field: 'sku',
      headerName: 'SKU',
    },
    { field: 'product_name', headerName: 'Product Name' },
    { field: 'description', headerName: 'Description' },
    {
      field: 'price',
      headerName: 'Price',
      valueFormatter: currencyFormatter,
    },
    {
      field: 'sale_price',
      headerName: 'Sale Price',
      valueFormatter: currencyFormatter,
    },
    { field: 'budget_group', headerName: 'Budget Group' },
    {
      field: 'ad_status',
      headerName: 'Ad Status',
    },
    {
      field: 'reporting_visibility',
      headerName: 'Reporting Visibility',
    },
    {
      field: 'brand',
      headerName: 'Brand',
    },
    {
      field: 'availability',
      headerName: 'Availability',
    },
    {
      field: 'item_group_id',
      headerName: 'Item Group ID',
    },
    {
      field: 'size',
      headerName: 'Size',
    },
    {
      field: 'color',
      headerName: 'Color',
    },
    {
      field: 'condition',
      headerName: 'Condition',
    },
    {
      field: 'inventory',
      headerName: 'Inventory',
    },
    {
      field: 'gtin',
      headerName: 'GTIN',
    },
    {
      field: 'mpn',
      headerName: 'MPN',
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    cellStyle: (params) => {
      if (params && params.value < 0.00) {
        return { color: 'red' };
      }
      return null;
    },
  };

  // const frameworkComponents = {
  //   ...imageCellRendererComponent,
  // };
  const tableConfig = {
    showDatePicker: false,
    showFooter: false,
    parentControlledLoad: true,
    apiEndPoint: retailerConfig.endpoints.catalog(selectedSeller),
    paginationPageSize: pageSize,
    cacheBlockSize: pageSize,
  };

  // Helper function to fetch a specific page with token (for Google)
  const fetchGooglePage = async (token, headerWithAuth) => {
    const url = `${tableConfig.apiEndPoint}?maxResults=${pageSize}${token ? `&pageToken=${token}` : ''}`;
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };
    return doFetch(url, requestOptions);
  };

  // Helper function to fetch a specific page with cursor (for Meta)
  const fetchMetaPage = async (cursor, headerWithAuth) => {
    const url = `${tableConfig.apiEndPoint}?limit=${pageSize}${cursor ? `&after=${cursor}` : ''}`;
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };
    return doFetch(url, requestOptions);
  };

  // Sequential fetch helper for Google export
  // eslint-disable-next-line no-unused-vars
  const fetchGoogleSequentiallyUpTo = async (targetStartRow, headerWithAuth) => {
    let currentStartRow = 0;
    let token = null;

    while (currentStartRow < targetStartRow) {
      // Check if we already have this token cached
      if (tokenCacheRef.current[currentStartRow] !== undefined) {
        token = tokenCacheRef.current[currentStartRow];
        currentStartRow += pageSize;
      } else {
        // Fetch this page - sequential await is required for token-based pagination
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchGooglePage(token, headerWithAuth);

        // Cache the next token
        if (data.hasMore && data.nextPageToken) {
          tokenCacheRef.current[currentStartRow + pageSize] = data.nextPageToken;
          token = data.nextPageToken;
        } else {
          // No more pages
          break;
        }

        currentStartRow += pageSize;
      }
    }

    return tokenCacheRef.current[targetStartRow] || null;
  };

  const datasource = {
    getRows: async (params) => {
      const {
        request, success, fail,
      } = params;
      const { startRow, sortModel } = request;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
      const requestOptions = {
        method: 'GET',
        headers: headerWithAuth,
      };

      const offset = startRow || 0;
      const accountId = selectedSeller.parentAccountId > 0 ? selectedSeller.parentAccountId : selectedSeller.value;

      // Handle Google's token-based pagination
      if (retailerConfig.key === 'google') {
        try {
          // Get token for this page (may require sequential fetching)
          let token = tokenCacheRef.current[offset];

          // If we don't have the token and it's not the first page, fetch sequentially
          if (offset > 0 && token === undefined) {
            token = await fetchGoogleSequentiallyUpTo(offset, headerWithAuth);
          }

          // Fetch the data
          const data = await fetchGooglePage(token, headerWithAuth);

          // Cache next token if available
          if (data.hasMore && data.nextPageToken) {
            tokenCacheRef.current[offset + pageSize] = data.nextPageToken;
          }

          // Calculate row count for ag-Grid
          const rowCount = data.hasMore
            ? offset + data.results.length + 1 // +1 indicates more rows available
            : offset + data.results.length; // Final page

          success({
            rowData: data.results || [],
            rowCount,
          });
        } catch (error) {
          console.error('Google catalog fetch error:', error);
          fail();
        }
        return;
      }

      // Handle Meta's cursor-based pagination
      if (retailerConfig.key === 'meta') {
        try {
          // Get cursor for this page
          const cursor = cursorCacheRef.current[offset];

          // If we don't have the cursor and it's not the first page, we need to fetch sequentially
          // For now, only support forward navigation (cursor is cached from previous page)
          if (offset > 0 && cursor === undefined) {
            console.warn('Meta: Missing cursor for offset', offset);
            fail();
            return;
          }

          // Fetch the data
          const data = await fetchMetaPage(cursor, headerWithAuth);

          // Cache next cursor if available
          if (data.paging?.hasNext && data.paging?.cursors?.after) {
            cursorCacheRef.current[offset + pageSize] = data.paging.cursors.after;
          }

          // Use total count from API if available (like Walmart)
          // This prevents infinite loops by giving ag-Grid the exact total
          const rowCount = data.total || (data.results ? data.results.length : 0);

          success({
            rowData: data.results || [],
            rowCount,
          });
        } catch (error) {
          console.error('Meta catalog fetch error:', error);
          fail();
        }
        return;
      }

      // Handle TikTok's page-based pagination
      if (retailerConfig.key === 'tiktok') {
        try {
          const page = Math.floor(offset / pageSize) + 1;
          const url = `${tableConfig.apiEndPoint}?page=${page}&pageSize=${pageSize}`;
          const data = await doFetch(url, requestOptions);

          // TikTok returns total count and page info
          const rowCount = data.total || (data.results ? data.results.length : 0);

          success({
            rowData: data.results || [],
            rowCount,
          });
        } catch (error) {
          console.error('TikTok catalog fetch error:', error);
          fail();
        }
        return;
      }

      // Handle Amazon/Walmart offset-based pagination
      let sort = '';
      if (sortModel.length > 0) {
        sort = `${sortModel[0].colId} ${sortModel[0].sort}`;
      }

      const url = `${tableConfig.apiEndPoint}?accountId=${accountId}&currencyCode=${(selectedSeller.currencyCode || 'USD')}&accountTypeId=${selectedSeller.type}&limit=${tableConfig.paginationPageSize}&offset=${offset}&sort=${sort}`;
      try {
        const data = await doFetch(url, requestOptions);
        if (retailerConfig.key === 'amazon') {
          success({
            rowData: data,
            rowCount: data.length > 0 ? data[0].distinct_item_count : 0,
          });
        } else {
          success({
            rowData: data.results,
            rowCount: data.total,
          });
        }
      } catch (error) {
        fail();
      }
    },
  };

  function onFirstDataRendered(params) {
    params.api.sizeColumnsToFit();
  }

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  }

  useEffect(() => {
    if (agGrid) {
      agGrid.refreshData();
    }
  }, [forceRefresh]);

  // Refresh data when retailer changes (also reset token/cursor cache for Google/Meta)
  useEffect(() => {
    if (agGrid) {
      // Reset caches when switching retailers or accounts
      tokenCacheRef.current = { 0: null };
      cursorCacheRef.current = { 0: null };
      agGrid.refreshData();
    }
  }, [retailerConfig.key, selectedSeller.value, agGrid]);

  const changeShowEditModal = () => {
    setShowEditModal(!showEditModal);
  };

  const selectionChanged = (selectdData) => {
    setSelectedData(selectdData);
  };

  return (
    <Container className="todo-app">
      <Row>
        <CardPanel
          title="Catalog"
          subhead={new Date().toISOString()}
          autorenewButton={false}
          closeButton={false}
        >
          {(retailerConfig.key === 'google' || retailerConfig.key === 'meta') && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
              <Button
                size="sm"
                color="primary"
                outline
                onClick={() => refreshCatalog({
                  platform: retailerConfig.key,
                  accountId: selectedSeller.parentAccountId > 0 ? selectedSeller.parentAccountId : selectedSeller.value,
                  jwtToken: user.jwtToken,
                })}
              >
                Catalog Re-Sync
              </Button>
            </div>
          )}
          <TableBuilder
            columnDefs={(() => {
              if (retailerConfig.key === 'google') return googleColumnDefs;
              if (retailerConfig.key === 'meta') return metaColumnDefs;
              if (retailerConfig.key === 'tiktok') return tiktokColumnDefs;
              if (retailerConfig.key === 'amazon') return amzColumnDefs;
              if (retailerConfig.key === 'target') return targetColumnDefs;
              return wmColumnDefs;
            })()}
            defaultColDef={defaultColDef}
            apiEndPoint={tableConfig.apiEndPoint}
            showDatePicker={tableConfig.showDatePicker}
            showFooter={tableConfig.showFooter}
            // frameworkComponents={frameworkComponents}
            rowHeight={75}
            onFirstDataRendered={onFirstDataRendered}
            pagination
            rowModelType="serverSide"
            datasource={datasource}
            paginationPageSize={tableConfig.paginationPageSize}
            cacheBlockSize={tableConfig.cacheBlockSize}
            serverSideInfiniteScroll={retailerConfig.key !== 'meta' && retailerConfig.key !== 'tiktok'}
            parentControlledLoad={tableConfig.parentControlledLoad}
            editSelectedRowsButton={retailerConfig.key !== 'google' && retailerConfig.key !== 'meta' && retailerConfig.key !== 'tiktok'}
            openEditSelected={changeShowEditModal}
            parentSelectionChanged={selectionChanged}
            parentAccess={parentAccess}
            exportPrefix={exportPrefix}
          />
        </CardPanel>
        {retailerConfig.key === 'meta' && (
          <MetaProductSetsTable />
        )}
        <ItemEditModal
          changeShowEditModal={changeShowEditModal}
          showEditModal={showEditModal}
          theme={theme}
          selectedData={selectedData}
          setForceRefresh={setForceRefresh}
          budgetGroupOptions={budgetGroupOptions}
          setShowEditModal={setShowEditModal}
          retailerConfig={retailerConfig}
        />
      </Row>
    </Container>
  );
}

export default SimpleCatalogTable;
